import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError, ALL_FLOORING_TYPES, DISABLED_FLOORING, CONDITIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { UnitToggle } from "@/components/UnitToggle";
import { mToUnit, unitToM, unitLabel } from "@/lib/units";
import RetailerLayout from "@/components/RetailerLayout";
import PhotoStrip from "@/components/PhotoStrip";
import { StockTypeSelect } from "@/components/StockTypeSelect";
import { PriceCompareHint } from "@/components/PriceCompareHint";

/**
 * Full edit screen. Both retailers (own listing) and admins (any listing)
 * use the same page — the backend PATCH already enforces the permission
 * boundary. Status transitions are handled by dedicated buttons so the atomic
 * reservation guarantee is preserved.
 */
export default function RetailerListingEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const { unit } = useUnit();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Public listing endpoint already returns the fields we need to edit.
      // Fall back to admin path if the public one somehow fails (e.g. hidden).
      const r = await api.get(`/listings/${id}`).catch(() => null);
      if (r?.data) {
        setDoc({ ...r.data, photo_urls: r.data.photo_urls || (r.data.photo_url ? [r.data.photo_url] : []) });
      } else {
        // Admin fallback: fetch through admin listings
        const a = await api.get("/admin/listings");
        const found = (a.data.listings || []).find((x) => x.id === id);
        if (found) setDoc({ ...found, photo_urls: found.photo_urls || (found.photo_url ? [found.photo_url] : []) });
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  function set(k, v) {
    setDoc((d) => ({ ...d, [k]: v }));
  }

  async function save() {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      const coverageTypes = new Set(["laminate", "lvt", "carpet_tiles"]);
      const isCoverage = coverageTypes.has(doc.flooring_type);
      const payload = {
        flooring_type: doc.flooring_type,
        colour: (doc.colour || "").trim(),
        condition: doc.condition,
        matching_mode: isCoverage ? "coverage" : "dimensions",
        price_gbp: parseFloat(doc.price_gbp) || undefined,
        rrp_gbp: doc.rrp_gbp ? parseFloat(doc.rrp_gbp) : 0,
        photo_urls: doc.photo_urls || [],
        manufacturer: doc.manufacturer || null,
        range_name: doc.range_name || null,
        manufacturer_colour: doc.manufacturer_colour || null,
        product_code: doc.product_code || null,
        description: doc.description || null,
        status: ["draft", "live", "hidden"].includes(doc.status) ? doc.status : undefined,
      };
      if (isCoverage) {
        payload.coverage_per_pack_m2 = parseFloat(doc.coverage_per_pack_m2) || undefined;
        payload.packs_available =
          doc.packs_available !== "" && doc.packs_available !== null
            ? parseInt(doc.packs_available, 10)
            : undefined;
        payload.price_basis = doc.price_basis || "total_stock";
      } else {
        payload.width_m = parseFloat(doc.width_m) || undefined;
        payload.length_m = parseFloat(doc.length_m) || undefined;
      }
      payload.delivery_available = !!doc.delivery_available;
      payload.stock_type = doc.stock_type || "";
      if (doc.delivery_available && doc.delivery_fee_gbp !== "" && doc.delivery_fee_gbp != null) {
        payload.delivery_fee_gbp = parseFloat(doc.delivery_fee_gbp);
      }
      await api.patch(`/retailer/listings/${id}`, payload);
      toast.success("Listing saved");
      if (user?.role === "admin") {
        const retailerId = doc.retailer_id || doc.retailer?.id;
        nav(retailerId ? `/admin/retailers/${retailerId}` : "/admin");
      } else {
        nav("/retailer");
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function markSold() {
    if (!window.confirm("Mark this remnant as sold? Any active reservation will be closed.")) return;
    await api.post(`/retailer/listings/${id}/mark-sold`);
    toast.success("Marked sold");
    load();
  }
  async function markAvailable() {
    await api.post(`/retailer/listings/${id}/mark-available`);
    toast.success("Marked available");
    load();
  }
  async function confirmAvailable() {
    await api.post(`/retailer/listings/${id}/confirm-available`);
    toast.success("Confirmed as still available");
    load();
  }

  if (loading) return <RetailerLayout><div className="p-6 text-slate-500">Loading…</div></RetailerLayout>;
  if (!doc) return <RetailerLayout><div className="p-6 text-slate-500">Listing not found.</div></RetailerLayout>;

  const isCoverage = ["laminate", "lvt", "carpet_tiles"].includes(doc.flooring_type);
  const editBasis = !isCoverage ? "whole piece" : doc.price_basis === "per_pack" ? "per pack" : "all available stock";
  const area = !isCoverage && doc.width_m && doc.length_m ? (doc.width_m * doc.length_m).toFixed(2) : null;
  const totalCoverage =
    isCoverage && doc.coverage_per_pack_m2 && doc.packs_available
      ? (parseFloat(doc.coverage_per_pack_m2) * parseInt(doc.packs_available, 10)).toFixed(2)
      : null;

  return (
    <RetailerLayout>
      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6" data-testid="edit-listing-page">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#E05915] mb-3"
          data-testid="edit-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-xs font-mono text-slate-500">{doc.reference_code}</div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Edit listing</h1>
          </div>
          <StatusPill status={doc.status} />
        </div>

        <div className="grid gap-4">
          <Card title="Photos (up to 5)">
            <PhotoStrip
              photos={doc.photo_urls || []}
              onChange={(p) => set("photo_urls", p)}
            />
          </Card>

          <Card title="Basics">
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                <span className="fm-label">Flooring type</span>
                <select
                  value={doc.flooring_type}
                  onChange={(e) => set("flooring_type", e.target.value)}
                  className="fm-input"
                  data-testid="edit-flooring"
                >
                  {ALL_FLOORING_TYPES.filter((t) => !DISABLED_FLOORING.has(t.value) || t.value === doc.flooring_type).map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="fm-label">Colour</span>
                <input
                  value={doc.colour || ""}
                  onChange={(e) => set("colour", e.target.value)}
                  className="fm-input"
                  data-testid="edit-colour"
                />
              </label>
              {!isCoverage ? (
                <>
                  <label className="col-span-full flex items-center justify-between">
                    <span className="fm-label !mb-0">Dimensions</span>
                    <UnitToggle />
                  </label>
                  <label>
                    <span className="fm-label">Width ({unitLabel(unit)})</span>
                    <input
                      type="number" step="any" inputMode="decimal"
                      value={mToUnit(doc.width_m, unit)}
                      onChange={(e) => set("width_m", unitToM(e.target.value, unit) ?? "")}
                      className="fm-input"
                      data-testid="edit-width"
                    />
                  </label>
                  <label>
                    <span className="fm-label">Length ({unitLabel(unit)})</span>
                    <input
                      type="number" step="any" inputMode="decimal"
                      value={mToUnit(doc.length_m, unit)}
                      onChange={(e) => set("length_m", unitToM(e.target.value, unit) ?? "")}
                      className="fm-input"
                      data-testid="edit-length"
                    />
                  </label>
                  <div className="text-sm text-slate-600 col-span-full">
                    Area: <strong className="text-slate-900">{area ? `${area} m²` : "—"}</strong>
                  </div>
                </>
              ) : (
                <>
                  <label>
                    <span className="fm-label">Coverage per pack (m²)</span>
                    <input
                      type="number" step="0.01" inputMode="decimal"
                      value={doc.coverage_per_pack_m2 || ""}
                      onChange={(e) => set("coverage_per_pack_m2", e.target.value)}
                      className="fm-input"
                      data-testid="edit-coverage"
                    />
                  </label>
                  <label>
                    <span className="fm-label">Packs available</span>
                    <input
                      type="number" step="1" inputMode="numeric"
                      value={doc.packs_available ?? ""}
                      onChange={(e) => set("packs_available", e.target.value)}
                      className="fm-input"
                      data-testid="edit-packs"
                    />
                  </label>
                  <div className="text-sm text-slate-600 col-span-full">
                    Total available:{" "}
                    <strong className="text-slate-900">
                      {totalCoverage ? `${totalCoverage} m²` : "—"}
                    </strong>
                  </div>
                  <label className="col-span-full">
                    <span className="fm-label">Price applies to</span>
                    <select
                      value={doc.price_basis || "total_stock"}
                      onChange={(e) => set("price_basis", e.target.value)}
                      className="fm-input"
                      data-testid="edit-price-basis"
                    >
                      <option value="total_stock">All available stock</option>
                      <option value="per_pack">Per pack</option>
                    </select>
                  </label>
                </>
              )}
              <label>
                <span className="fm-label">Your selling price (£, {editBasis})</span>
                <input
                  type="number" step="0.01" inputMode="decimal"
                  value={doc.price_gbp || ""}
                  onChange={(e) => set("price_gbp", e.target.value)}
                  className="fm-input"
                  data-testid="edit-price"
                />
              </label>
              <label>
                <span className="fm-label">Retail price (£, {editBasis}, optional)</span>
                <input
                  type="number" step="0.01" inputMode="decimal"
                  value={doc.rrp_gbp || ""}
                  onChange={(e) => set("rrp_gbp", e.target.value)}
                  className="fm-input"
                  data-testid="edit-rrp"
                />
              </label>
              <div className="col-span-full -mt-2">
                <PriceCompareHint price={doc.price_gbp} retail={doc.rrp_gbp} basis={editBasis} />
              </div>
              <label>
                <span className="fm-label">Condition</span>
                <select
                  value={doc.condition}
                  onChange={(e) => set("condition", e.target.value)}
                  className="fm-input"
                  data-testid="edit-condition"
                >
                  {CONDITIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="fm-label">Availability</span>
                <select
                  value={["live", "hidden", "draft"].includes(doc.status) ? doc.status : "live"}
                  onChange={(e) => set("status", e.target.value)}
                  className="fm-input"
                  disabled={["reserved", "sold"].includes(doc.status)}
                  data-testid="edit-status"
                >
                  <option value="live">Live</option>
                  <option value="hidden">Hidden</option>
                  <option value="draft">Draft</option>
                </select>
              </label>
            </div>
          </Card>

          <Card title="Why it's discounted">
            <StockTypeSelect value={doc.stock_type} onChange={(v) => set("stock_type", v)} testId="edit-stock-type" />
          </Card>

          <Card title="Collection & delivery (optional)">
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!doc.delivery_available}
                onChange={(e) => set("delivery_available", e.target.checked)}
                className="mt-1 accent-[#E05915]"
                data-testid="edit-delivery-available"
              />
              <span>
                <span className="font-semibold text-slate-900">Offer collection &amp; delivery</span> —
                a driver brings it to the customer. The fee is paid directly to the driver.
              </span>
            </label>
            {doc.delivery_available && (
              <label className="block mt-3 max-w-xs">
                <span className="fm-label">Delivery fee (£)</span>
                <input
                  type="number" step="0.01" inputMode="decimal"
                  value={doc.delivery_fee_gbp ?? ""}
                  onChange={(e) => set("delivery_fee_gbp", e.target.value)}
                  className="fm-input"
                  data-testid="edit-delivery-fee"
                />
              </label>
            )}
          </Card>

          <Card title="Product info (optional)">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Manufacturer / brand" val={doc.manufacturer} onChange={(v) => set("manufacturer", v)} testId="edit-manufacturer" />
              <Field label="Range" val={doc.range_name} onChange={(v) => set("range_name", v)} testId="edit-range" />
              <Field label="Manufacturer's colour" val={doc.manufacturer_colour} onChange={(v) => set("manufacturer_colour", v)} testId="edit-mcolour" />
              <Field label="Product / reference code" val={doc.product_code} onChange={(v) => set("product_code", v)} testId="edit-productcode" />
            </div>
          </Card>

          <Card title="Description">
            <textarea
              rows={4}
              value={doc.description || ""}
              onChange={(e) => set("description", e.target.value)}
              className="fm-input min-h-[100px] py-2"
              data-testid="edit-description"
            />
          </Card>

          <Card title="Quick actions">
            <div className="flex flex-wrap gap-2">
              <ActionBtn onClick={confirmAvailable} testId="edit-confirm-available">
                <CheckCircle2 className="w-4 h-4" /> Still available ✓
              </ActionBtn>
              {doc.status === "live" && (
                <ActionBtn onClick={markSold} testId="edit-mark-sold">
                  Mark sold
                </ActionBtn>
              )}
              {(doc.status === "sold" || doc.status === "reserved") && (
                <ActionBtn onClick={markAvailable} testId="edit-mark-available">
                  Mark available
                </ActionBtn>
              )}
              <Link
                to={`/listing/${id}`}
                className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-md bg-white border border-slate-200 hover:bg-slate-50"
                data-testid="edit-view-public"
              >
                View public
              </Link>
            </div>
            {doc.last_confirmed_available_at && (
              <div className="mt-3 text-xs text-slate-500" data-testid="last-confirmed">
                Last confirmed available: {new Date(doc.last_confirmed_available_at).toLocaleString("en-GB")}
              </div>
            )}
          </Card>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>
          )}

          <div className="sticky bottom-0 -mx-3 sm:mx-0 bg-white/95 backdrop-blur border-t sm:border sm:rounded-2xl border-slate-200 p-3 flex gap-2 z-20">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="fm-btn-primary flex-1 disabled:opacity-70"
              data-testid="edit-save"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </main>
    </RetailerLayout>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4">
      <h2 className="font-display font-semibold text-slate-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}
function Field({ label, val, onChange, testId }) {
  return (
    <label>
      <span className="fm-label">{label}</span>
      <input
        value={val || ""}
        onChange={(e) => onChange(e.target.value)}
        className="fm-input"
        data-testid={testId}
      />
    </label>
  );
}
function ActionBtn({ children, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200"
      data-testid={testId}
    >
      {children}
    </button>
  );
}
function StatusPill({ status }) {
  const map = {
    live: "bg-emerald-100 text-emerald-800",
    reserved: "bg-amber-100 text-amber-800",
    sold: "bg-slate-200 text-slate-700",
    expired: "bg-red-100 text-red-800",
    hidden: "bg-slate-100 text-slate-500",
    draft: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100"}`}>
      {status}
    </span>
  );
}
